import { FunctionComponent, memo, useCallback, useMemo, useState } from "react";
import { IProps } from "./filter-fields.types";
import "./filter-fields.scss";
import { FieldFactory } from "../field-factory";
import { FieldInline } from "@edgetier/client-components";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { Select } from "@edgetier/select";
import { IFilter } from "~/filters.types";
import { getFilterName, getNotVisibleFields, getVisibleFields } from "~/filters.utilities";
import classNames from "classnames";
import { useFormikContext } from "formik";

/**
 * Component for managing filter fields.
 * @param {object[]} props.visibleFields - The array of visible filter configurations.
 * @param {boolean} props.shouldShowAddFiltersButton - Flag to show the add filters button.
 * @param {string[]} props.fixedFilters - The fixed filter IDs.
 * @param {function} props.onRemove - Function to remove a filter.
 * @param {function} props.onSelect - Function to select a filter.
 * @param {object[]} props.selectItems - The array of filter configurations to select from.
 * @returns {JSX.Element} The filter fields component.
 */
const FilterFields: FunctionComponent<IProps> = ({
    filters,
    visibleFilters: visibleFiltersProp,
    fixedFilters,
    className,
    style,
    children,
    locked,
    gridType = "extended",
}) => {
    const { setFieldValue } = useFormikContext();
    const [visibleFilterIds, setVisibleFilterIds] = useState<string[]>(visibleFiltersProp);
    const visibleFields = useMemo<IFilter[]>(
        () => getVisibleFields(visibleFilterIds, filters),
        [visibleFilterIds, filters]
    );
    const notVisible = useMemo(() => getNotVisibleFields(filters, visibleFilterIds), [filters, visibleFilterIds]);
    const shouldShowAddFiltersButton = useMemo(
        () => visibleFilterIds.length < filters.length,
        [visibleFilterIds, filters]
    );
    const handleAddField = useCallback((id: string) => {
        setVisibleFilterIds((visibleFilters) => [...visibleFilters, id]);
    }, []);
    const handleRemoveField = useCallback((id: string) => {
        setVisibleFilterIds((visibleFilters) => [...visibleFilters.filter((visibleId) => visibleId !== id)]);
    }, []);

    return (
        <div
            className={classNames("filter-fields", className, {
                "filter-fields--compact": gridType === "compact",
                "filter-fields--extended": gridType === "extended",
            })}
            style={style}
        >
            {visibleFields?.map((filter, index) => (
                <FieldFactory
                    canBeRemoved={!fixedFilters?.includes(filter.id)}
                    onRemove={() => {
                        setFieldValue(getFilterName(filter), undefined);
                        handleRemoveField(filter.id);
                    }}
                    key={`field-${index}`}
                    field={filter}
                />
            ))}
            {!locked && shouldShowAddFiltersButton && (
                <FieldInline icon={faPlus}>
                    <Select<IFilter, string>
                        data-testid="filters-add-filter"
                        selectedValues={null}
                        getLabel={({ label }) => label}
                        getValue={({ id }) => id}
                        items={notVisible}
                        onSelect={handleAddField}
                        description="filter"
                        previewPlaceholder={"Add filters"}
                        isSingleSelect={true}
                        placeholder="Search filter&hellip;"
                    />
                </FieldInline>
            )}
            {children}
        </div>
    );
};

export default memo(FilterFields);
